import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import SectionMessage from '@atlaskit/section-message/message';
import { token } from '@atlaskit/tokens';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { useContentByLabelData } from './hooks/useContentByLabelData.ts';
import {
	useParsedMacroParams,
	type ContentByLabelMacroParams,
} from './hooks/useParsedMacroParams.ts';
import { useProcessedContentByLabelsResult } from './hooks/useProcessedContentByLabelsResult.ts';
import { i18n } from './contentByLabelMacroI18n.ts';
import { ContentEntry } from './components/ContentEntry.tsx';
import { EmptyState } from './components/EmptyState.tsx';
import { LoadingState } from './components/LoadingState.tsx';

export type ContentByLabelMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: ContentByLabelMacroParams;
	RendererWrapper: NestedAdfRendererWrapper;
};

const styles = cssMap({
	list: {
		marginBlock: token('space.100'),
		paddingInlineStart: token('space.0'),
		listStyleType: 'none',
	},
	invalidInputErrorDetail: {
		overflowWrap: 'anywhere',
	},
});

const BAD_REQUEST_EXCEPTION_ERROR_MESSAGE = 'BadRequestException';

const isBadRequestExceptionError = (error: Error): boolean =>
	error.message.includes(BAD_REQUEST_EXCEPTION_ERROR_MESSAGE);

const ContentByLabelMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
	RendererWrapper,
}: ContentByLabelMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const {
		cql,
		max,
		maxParseError,
		title,
		showLabels,
		showSpace,
		excerptType,
		limitLabelLinksToSpace,
		macroParamsForQuery,
	} = useParsedMacroParams(macroParams, macroDefaultProps?.spaceKey, contentId);

	const {
		data: queryData,
		error,
		loading,
		hasNextPage,
		isLoadingMore,
		loadMore,
		loadMoreError,
	} = useContentByLabelData(macroParamsForQuery, max);

	const processedData = useProcessedContentByLabelsResult(queryData);
	const items = processedData?.items;

	if (!cql) {
		const labelsRequiredError = new Error(formatMessage(i18n.errorLabelsRequired));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={labelsRequiredError}
				/>
				<ErrorDisplay error={labelsRequiredError} />
			</>
		);
	}

	if (maxParseError) {
		const parseMaxError = new Error(formatMessage(i18n.errorParseMax));
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={parseMaxError}
				/>
				<ErrorDisplay error={parseMaxError} />
			</>
		);
	}

	if (loading) {
		return <LoadingState />;
	}

	if (error) {
		if (isBadRequestExceptionError(error)) {
			markErrorAsHandled(error);

			return (
				<>
					<MacroExperienceSuccess
						name={experienceName}
						contentId={contentId}
						extensionKey={extensionKey}
						mode={mode}
						attributes={attributes}
					/>
					<SectionMessage title={formatMessage(i18n.errorInvalidInput)} appearance="error">
						<Text as="p" color="color.text.subtle" xcss={styles.invalidInputErrorDetail}>
							{error.message}
						</Text>
					</SectionMessage>
				</>
			);
		}

		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={error}
				/>
				<ErrorDisplay error={error} />
			</>
		);
	}

	if (!items || items.length === 0) {
		const isEditorPreview = macroDefaultProps?.mode === 'editor';
		return (
			<>
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
				<EmptyState title={title} showEditCTA={isEditorPreview} />
			</>
		);
	}

	return (
		<>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
			<Box
				testId="content-by-label-macro"
				data-fabric-macro={macroDefaultProps?.attributes?.macroId}
				data-macro-body=""
			>
				{title && <Heading size="medium">{title}</Heading>}
				<Stack as="ul" xcss={styles.list}>
					{items.map((item, index) => (
						<ContentEntry
							key={item.id}
							item={item}
							showLabels={showLabels}
							showSpace={showSpace}
							excerptType={excerptType}
							limitLabelLinksToSpace={limitLabelLinksToSpace}
							isFirst={index === 0}
							RendererWrapper={RendererWrapper}
						/>
					))}
				</Stack>
				{hasNextPage && loadMore && (
					<Box paddingBlockStart="space.200">
						<Button onClick={loadMore} isLoading={isLoadingMore}>
							<FormattedMessage {...i18n.loadMore} />
						</Button>
					</Box>
				)}
				{loadMoreError && (
					<ErrorDisplay error={loadMoreError}>
						<Box paddingBlockStart="space.100">
							<SectionMessage appearance="error">
								<FormattedMessage {...i18n.errorSearch} />
							</SectionMessage>
						</Box>
					</ErrorDisplay>
				)}
			</Box>
		</>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const ContentByLabelMacroRenderer = (
	props: ContentByLabelMacroRendererProps,
): React.JSX.Element => {
	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXTENSIONS}
		>
			<ContentByLabelMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
